// src/frontend/pages/ContentViewerBlockchain.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

/* ====== IMPORTA SOLO DATOS (objetos), no componentes ======
   Usa SIEMPRE el nombre correcto de carpeta:
   - Si tu carpeta es "FundamentosBlockchain", corrige los imports debajo.
   - Aquí mantengo "FundametosBlockchain" tal como lo tenías para no romper.
*/
import INTRO from "../data/books/FundametosBlockchain/data/cap1"; // <- resumen con títulos/subtítulos/tablas
import CAP2 from "../data/books/FundametosBlockchain/data/cap2";
import CAP3 from "../data/books/FundametosBlockchain/data/cap3";
import CAP4 from "../data/books/FundametosBlockchain/data/cap4";
import CAP5 from "../data/books/FundametosBlockchain/data/cap5";
import CAP6 from "../data/books/FundametosBlockchain/data/cap6";
import CAP7 from "../data/books/FundametosBlockchain/data/cap7";
import CAP8 from "../data/books/FundametosBlockchain/data/cap8";
import CAP9 from "../data/books/FundametosBlockchain/data/cap9";
import CAP10 from "../data/books/FundametosBlockchain/data/cap10";
import CAP11 from "../data/books/FundametosBlockchain/data/cap11";
import CAP12 from "../data/books/FundametosBlockchain/data/cap12";
import CAP13 from "../data/books/FundametosBlockchain/data/cap13";
import CAP14 from "../data/books/FundametosBlockchain/data/cap14";

/** Registro de “construcciones” */
const ComponentRegistry = {
  IntroBlockchain: () => <ChapterRenderer data={INTRO} />,
  BloquesHashesMerkle: () => <ChapterRenderer data={CAP2} />,
  ConsensoPoWPoS: () => <ChapterRenderer data={CAP3} />,
  CarterasClavesFirmas: () => <ChapterRenderer data={CAP4} />,
  RedesNodos: () => <ChapterRenderer data={CAP5} />,
  CasosUsoLimitaciones: () => <ChapterRenderer data={CAP6} />,
  Glosario: () => <ChapterRenderer data={CAP7} />,
  cap8: () => <ChapterRenderer data={CAP8} />,
  cap9: () => <ChapterRenderer data={CAP9} />,
  cap10: () => <ChapterRenderer data={CAP10} />,
  cap11: () => <ChapterRenderer data={CAP11} />,
  cap12: () => <ChapterRenderer data={CAP12} />,
  cap13: () => <ChapterRenderer data={CAP13} />,
  cap14: () => <ChapterRenderer data={CAP14} />,
};

/** Índice lateral (sidebar) */
const contentsData = [
  { id: 1, title: "Fundamentos de Blockchain ", type: "external", component: "IntroBlockchain" },
  { id: 2, title: "Origen y Evolución de Blockchain", type: "external", component: "BloquesHashesMerkle" },
  { id: 3, title: "¿Qué es Blockchain?", type: "external", component: "ConsensoPoWPoS" },
  { id: 4, title: "Tipos de Blockchains", type: "external", component: "CarterasClavesFirmas" },
  { id: 5, title: "Aplicaciones de la Tecnología Blockchain", type: "external", component: "RedesNodos" },
  { id: 6, title: "Bitcoin: Origen, Funcionamiento de la Primera Criptomoneda", type: "external", component: "CasosUsoLimitaciones" },
  { id: 7, title: "Arquitectura Distribuida y Modelos de Almacenamiento en Blockchain", type: "external", component: "Glosario" },
  { id: 8, title: "Redes Peer-to-Peer (P2P) y su papel en Blockchain", type: "external", component: "cap8" },
  { id: 9, title: "Sistemas Sin Necesidad de Confianza: el Consenso como Sustituto de la Fe Humana", type: "external", component: "cap9" },
  { id: 10, title: "Alta Disponibilidad en Blockchain: Resiliencia, Redundancia y Continuidad Global", type: "external", component: "cap10" },
  { id: 11, title: "Inmutabilidad en Blockchain: Garantía de Integridad y Confianza Permanente", type: "external", component: "cap11" },
  { id: 12, title: "Anonimato y Privacidad en Blockchain: Identidad Criptográfica y Confianza Sin Revelación", type: "external", component: "cap12" },
  { id: 13, title: "Conclusiones y Perspectivas Futuras de la Tecnología Blockchain", type: "external", component: "cap14" },
];

/* ====== Estilo/UX ====== */
const TITLE_BLUE = "#175cd3";
const SIDEBAR_BG = "#0f2147";
const SIDEBAR_BORDER = "#0b1a3a";
const BASE_PATH = "/course/blockchain-fundamentos";

/* ---------- Utils ---------- */
function slugify(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function ensureAnchorId(section, fallbackPrefix = "sec") {
  if (section.anchor) return section.anchor;
  if (section.id) return section.id;
  if (section.heading) return slugify(section.heading);
  return `${fallbackPrefix}-${Math.random().toString(36).slice(2, 8)}`;
}

/* ---------- Resolver rutas de imagen de forma robusta ---------- */
function resolveAssetSrc(raw) {
  if (!raw) return "";
  const isHttp = /^https?:\/\//i.test(raw);
  if (isHttp) return raw;

  const viteBase =
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.BASE_URL) ||
    "";
  const craBase = typeof process !== "undefined" ? process.env.PUBLIC_URL || "" : "";
  const basePrefix = viteBase || craBase || "";

  if (raw.startsWith("/")) {
    return `${basePrefix.replace(/\/$/, "")}${raw}`;
  }

  try {
    const imported = new URL(`../assets/${raw}`, import.meta.url).href;
    return imported;
  } catch {
    return raw;
  }
}

/** ====== Markdown-lite + Glosario ====== */
// Carga dinámica del glosario (cap15). Si no existe, sigue sin romper.
// Carga del glosario EXACTA + expansión de alias (paréntesis) + límites Unicode
function useGlossaryIndex() {
  const [index, setIndex] = React.useState({ regex: null, dict: {}, size: 0 });


  
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // 👇 Ruta exacta donde nos dijiste que está el archivo:
        const mod = await import("../data/books/FundamentosBlockchain/data/cap15");

        // Acepta tanto sections[].glossary como default.glossary
        const rootGloss = Array.isArray(mod?.default?.glossary) ? mod.default.glossary : [];
        const secGloss = Array.isArray(mod?.default?.sections)
          ? (mod.default.sections.find(s => Array.isArray(s.glossary))?.glossary ?? [])
          : [];
        const glossary = rootGloss.length ? rootGloss : secGloss;

        if (!glossary?.length) {
          if (mounted) setIndex({ regex: null, dict: {}, size: 0 });
          return;
        }

        // Construye diccionario y ALIAS a partir de paréntesis
        // Ej.: "Address (Dirección)" -> "Address", "Dirección", "Address (Dirección)"
        const dict = {};
        const add = (term, def) => {
          const t = String(term || "").trim();
          if (!t) return;
          dict[t.toLowerCase()] = def;
        };

        for (const item of glossary) {
          const term = String(item?.termino ?? "").trim();
          const def = String(item?.definicion ?? "").trim();
          if (!term || !def) continue;

          add(term, def);

          // Expansión por paréntesis
          // "Foo (Bar)" -> "Foo" y "Bar"
          const m = term.match(/^(.*?)\s*\((.*?)\)\s*$/);
          if (m) {
            const left = m[1].trim();
            const inside = m[2].trim();
            if (left) add(left, def);
            if (inside) add(inside, def);
          }
        }

        const keys = Object.keys(dict);
        if (!keys.length) {
          if (mounted) setIndex({ regex: null, dict: {}, size: 0 });
          return;
        }

        // Ordena por largo (para preferir términos largos) y crea patrón Unicode
        const escaped = keys.sort((a,b)=>b.length-a.length).map(escapeRegex);
        const group = `(?:${escaped.join("|")})`;
        // Límite: no letra/número/_ a ambos lados
        const boundary = `(?<![\\p{L}\\p{N}_])${group}(?![\\p{L}\\p{N}_])`;
        const regex = new RegExp(boundary, "giu");

        if (mounted) setIndex({ regex, dict, size: keys.length });
      } catch (e) {
        console.warn("[Glosario] No se pudo cargar cap15.js en FundamentosBlockchain:", e);
        if (mounted) setIndex({ regex: null, dict: {}, size: 0 });
      }
    })();

    return () => { mounted = false; };
  }, []);

  return index;
}


function escapeRegex(s="") {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Tokeniza inline preservando seguridad (sin dangerouslySetInnerHTML) */
function RichText({ text = "", glossaryIndex }) {
  const codeChunks = splitByCode(text);
  return (
    <>
      {codeChunks.map((chunk, i) => {
        if (chunk.type === "code") {
          return <code key={`c${i}`} className="vv-inline-code">{chunk.text}</code>;
        }
        const boldChunks = splitByBold(chunk.text);
        return boldChunks.map((b, j) => {
          const nodes = highlightGlossary(b.text, glossaryIndex);
          if (b.bold) {
            return <strong key={`b${i}-${j}`}>{nodes}</strong>;
          }
          return <React.Fragment key={`t${i}-${j}`}>{nodes}</React.Fragment>;
        });
      })}
    </>
  );
}

function splitByCode(str="") {
  const parts = [];
  let rest = str;
  let idx = 0;
  while (true) {
    const start = rest.indexOf("`");
    if (start < 0) { parts.push({ type: "text", text: rest }); break; }
    const end = rest.indexOf("`", start + 1);
    if (end < 0) { parts.push({ type: "text", text: rest }); break; }
    const before = rest.slice(0, start);
    const code = rest.slice(start + 1, end);
    if (before) parts.push({ type: "text", text: before });
    parts.push({ type: "code", text: code });
    rest = rest.slice(end + 1);
    idx++;
    if (idx > 2000) break;
  }
  return parts;
}

function splitByBold(str="") {
  const out = [];
  let rest = str;
  let guard = 0;
  while (true) {
    const s = rest.indexOf("**");
    if (s < 0) { if (rest) out.push({ bold:false, text: rest }); break; }
    const e = rest.indexOf("**", s + 2);
    if (e < 0) { out.push({ bold:false, text: rest }); break; }
    const before = rest.slice(0, s);
    const bold = rest.slice(s + 2, e);
    if (before) out.push({ bold:false, text: before });
    out.push({ bold:true, text: bold });
    rest = rest.slice(e + 2);
    guard++; if (guard > 2000) break;
  }
  return out;
}

/** Resalta términos del glosario con tooltip */
function highlightGlossary(str="", glossaryIndex) {
  const { regex, dict } = glossaryIndex || {};
  if (!regex) return str;

  const nodes = [];
  let lastIndex = 0;
  const text = String(str);

  regex.lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const start = m.index;
    const end = regex.lastIndex;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const term = m[0];
    const def = dict[term.toLowerCase()];
    nodes.push(
      <abbr key={`${start}-${end}`} className="vv-gloss" title={def || term}>
        {term}
      </abbr>
    );
    lastIndex = end;
    if (nodes.length > 4000) break;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/* ====== Renderer local con TOC, subsections, images y tables ====== */
function ChapterRenderer({ data }) {
  const glossaryIndex = useGlossaryIndex();

  const safeData = data ?? { title: "", sections: [] };
  const { title, sections = [] } = safeData;

  const processedSections = useMemo(() => {
    const slugifyLocal = (str = "") =>
      String(str)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

    const ensureAnchorIdLocal = (section, fallbackPrefix = "sec") => {
      if (section.anchor) return section.anchor;
      if (section.id) return section.id;
      if (section.heading) return slugifyLocal(section.heading);
      return `${fallbackPrefix}-${Math.random().toString(36).slice(2, 8)}`;
    };

    return (sections || []).map((s, idx) => {
      const anchor = ensureAnchorIdLocal(s, `sec-${idx + 1}`);
      const subsections = Array.isArray(s.subsections)
        ? s.subsections.map((sub, j) => ({
            ...sub,
            _anchor: ensureAnchorIdLocal(sub, `sub-${idx + 1}-${j + 1}`),
          }))
        : [];
      return { ...s, _anchor: anchor, subsections };
    });
  }, [sections]);

  const tocItems = useMemo(() => {
    return processedSections.map((s) => ({
      anchor: s._anchor,
      label: s.heading || "Sección",
      subs: (s.subsections || []).map((sub) => ({
        anchor: sub._anchor,
        label: sub.heading || "Subsección",
      })),
    }));
  }, [processedSections]);

  const jump = (anchor) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.scrollBy({ top: -80, left: 0, behavior: "instant" });
    }
  };

  const hasContent = Boolean(title) || (sections && sections.length > 0);

  return (
    <article className="vv-article">
      {!hasContent ? (
        <div className="vv-card">
          <p style={{ margin: 0, color: "#475569" }}>No hay datos para este capítulo.</p>
        </div>
      ) : (
        <>
          {title && <h2>{title}</h2>}

          {tocItems.length > 0 && (
            <nav className="vv-card vv-toc" aria-label="Índice del capítulo">
              <h3 style={{ marginTop: 0 }}>Índice</h3>
              <ol className="vv-toc-list">
                {tocItems.map((t) => (
                  <li key={t.anchor}>
                    <button className="vv-toc-link" onClick={() => jump(t.anchor)}>
                      {t.label}
                    </button>
                    {t.subs?.length > 0 && (
                      <ol className="vv-toc-sublist">
                        {t.subs.map((sb) => (
                          <li key={sb.anchor}>
                            <button className="vv-toc-link" onClick={() => jump(sb.anchor)}>
                              {sb.label}
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {processedSections.map((s) => (
            <section key={s._anchor} className="vv-card" id={s._anchor}>
              {s.heading && <h3>{s.heading}</h3>}

              {(() => {
                const img =
                  s?.image?.src
                    ? { src: s.image.src, alt: s.image.alt, caption: s.image.caption }
                    : s?.type === "image" && s?.src
                      ? { src: s.src, alt: s.alt, caption: s.caption }
                      : null;

                if (!img) return null;
                const resolved = resolveAssetSrc(img.src);

                return (
                  <figure className="vv-figure">
                    <img
                      src={resolved}
                      alt={img.alt || ""}
                      onError={() => console.warn("[Imagen] No se pudo cargar:", resolved)}
                    />
                    {img.caption && <figcaption>{img.caption}</figcaption>}
                  </figure>
                );
              })()}

              {typeof s.text === "string"
                ? s.text.split("\n").map((p, i) =>
                    p.trim()
                      ? (
                        <p key={i}>
                          <RichText text={p} glossaryIndex={glossaryIndex} />
                        </p>
                        )
                      : <br key={`br-${i}`} />
                  )
                : Array.isArray(s.text)
                  ? s.text.map((p, i) => (
                      <p key={i}>
                        <RichText text={p} glossaryIndex={glossaryIndex} />
                      </p>
                    ))
                  : null}

              {Array.isArray(s.list) && s.list.length > 0 && (
                <ul className="vv-list">
                  {s.list.map((li, idx) => (
                    <li key={idx}>
                      <RichText text={String(li)} glossaryIndex={glossaryIndex} />
                    </li>
                  ))}
                </ul>
              )}

              {s.table?.columns?.length > 0 && Array.isArray(s.table.rows) && (
                <div className="vv-table-wrap">
                  <table className="vv-table" role="table">
                    <thead>
                      <tr>
                        {s.table.columns.map((c) => (
                          <th key={c} scope="col">
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {s.table.rows.map((row, idx) => {
                        if (Array.isArray(row)) {
                          return (
                            <tr key={idx}>
                              {row.map((cell, j) => (
                                <td key={j}>
                                  <RichText text={String(cell)} glossaryIndex={glossaryIndex} />
                                </td>
                              ))}
                            </tr>
                          );
                        }
                        return (
                          <tr key={idx}>
                            {s.table.columns.map((c) => (
                              <td key={c}>
                                <RichText text={String(row[c] ?? "")} glossaryIndex={glossaryIndex} />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {s.code && (
                <pre className="vv-code small">
                  <code>{s.code}</code>
                </pre>
              )}

              {Array.isArray(s.subsections) &&
                s.subsections.map((sub) => (
                  <div key={sub._anchor} id={sub._anchor} className="vv-subsection">
                    {sub.heading && <h4>{sub.heading}</h4>}

                    {(() => {
                      const img =
                        sub?.image?.src
                          ? { src: sub.image.src, alt: sub.image.alt, caption: sub.image.caption }
                          : sub?.type === "image" && sub?.src
                            ? { src: sub.src, alt: sub.alt, caption: sub.caption }
                            : null;

                      if (!img) return null;
                      const resolved = resolveAssetSrc(img.src);

                      return (
                        <figure className="vv-figure">
                          <img
                            src={resolved}
                            alt={img.alt || ""}
                            onError={() =>
                              console.warn("[Imagen] No se pudo cargar:", resolved)
                            }
                          />
                          {img.caption && <figcaption>{img.caption}</figcaption>}
                        </figure>
                      );
                    })()}

                    {typeof sub.text === "string"
                      ? sub.text.split("\n").map((p, i) =>
                          p.trim()
                            ? (
                              <p key={i}>
                                <RichText text={p} glossaryIndex={glossaryIndex} />
                              </p>
                              )
                            : <br key={`br-s-${i}`} />
                        )
                      : Array.isArray(sub.text)
                        ? sub.text.map((p, i) => (
                            <p key={i}>
                              <RichText text={p} glossaryIndex={glossaryIndex} />
                            </p>
                          ))
                        : null}

                    {Array.isArray(sub.list) && sub.list.length > 0 && (
                      <ul className="vv-list">
                        {sub.list.map((li, idx) => (
                          <li key={idx}>
                            <RichText text={String(li)} glossaryIndex={glossaryIndex} />
                          </li>
                        ))}
                      </ul>
                    )}

                    {sub.table?.columns?.length > 0 &&
                      Array.isArray(sub.table.rows) && (
                        <div className="vv-table-wrap">
                          <table className="vv-table" role="table">
                            <thead>
                              <tr>
                                {sub.table.columns.map((c) => (
                                  <th key={c} scope="col">
                                    {c}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sub.table.rows.map((row, idx) => {
                                if (Array.isArray(row)) {
                                  return (
                                    <tr key={idx}>
                                      {row.map((cell, j) => (
                                        <td key={j}>
                                          <RichText text={String(cell)} glossaryIndex={glossaryIndex} />
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                }
                                return (
                                  <tr key={idx}>
                                    {sub.table.columns.map((c) => (
                                      <td key={c}>
                                        <RichText text={String(row[c] ?? "")} glossaryIndex={glossaryIndex} />
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                    {sub.code && (
                      <pre className="vv-code small">
                        <code>{sub.code}</code>
                      </pre>
                    )}
                  </div>
                ))}
            </section>
          ))}
        </>
      )}
    </article>
  );
}

export default function ContentViewerBlockchain() {
  const { contentId } = useParams();
  const navigate = useNavigate();

  const [selected, setSelected] = useState(
    () => contentsData.find((c) => c.id === Number(contentId)) || contentsData[0]
  );

  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const found = contentsData.find((c) => c.id === Number(contentId));
    if (found) setSelected(found);
  }, [contentId]);

  const handleSelect = (item) => navigate(`${BASE_PATH}/${item.id}`);

  return (
    <div className="vv-serif-root vv-wrap" data-collapsed={collapsed ? "true" : "false"}>
      <style>{`
  :root { --header-offset: 11vh; }

  .vv-wrap{
    --sb-w-expanded: 340px;
    --sb-w-collapsed: 84px;
    --sb-w: var(--sb-w-expanded);
    display:block;
    min-height:100dvh;
    background:#fff;
  }
  .vv-wrap[data-collapsed="true"]{ --sb-w: var(--sb-w-collapsed); }

  .vv-sidebar{
    position:fixed;
    inset: var(--header-offset) auto 0 0;
    width:var(--sb-w);
    background:${SIDEBAR_BG};
    border-right:1px solid ${SIDEBAR_BORDER};
    display:grid;
    grid-template-rows:auto 1fr auto;
    z-index:30;
    overflow:hidden;
  }
  .vv-side-scroll{ overflow:auto; padding:10px 10px 16px 10px; }

  .vv-title{
    display:flex; align-items:center; gap:10px;
    background:${TITLE_BLUE}; color:#fff; font-weight:800;
    font-size:16px; padding:16px 14px; margin:0;
    border-bottom:1px solid rgba(255,255,255,.15);
    white-space:nowrap; position:relative;
  }
  .vv-collapse{
    position:absolute; top:12px; right:12px;
    width:34px; height:34px; border-radius:999px;
    border:1px solid rgba(255,255,255,.25);
    background:#132a55; color:#fff; font-weight:800; cursor:pointer;
  }

  .vv-items{ list-style:none; margin:8px 0 0; padding:0; }
  .vv-itemBtn{
     all:unset;
  display:grid; grid-template-columns:40px 1fr auto; align-items:center; gap:10px;
  width:100%; padding:10px 12px; margin:2px 6px; cursor:pointer; border-radius:10px;
  color:#c9ddff;
  }
  .vv-itemBtn:hover{ background:#132a55; }
  .vv-itemActive{ background:#132a55; outline:1px solid rgba(255,255,255,.08); color:#fff; }



  .vv-itemLabel{
    font-size:14px; line-height:1.25;
    display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
  }

  .vv-wrap[data-collapsed="true"] .vv-itemLabel{ display:none; }
  .vv-wrap[data-collapsed="true"] .vv-itemBtn{ grid-template-columns:1fr; justify-items:center; }
  .vv-wrap[data-collapsed="true"] .vv-title{ font-size:0; padding-right:52px; }
  .vv-wrap[data-collapsed="true"] .vv-title::after{
    content:"B"; font-size:16px; font-weight:900; letter-spacing:.1em;
  }

  .vv-side-footer{ border-top:1px solid ${SIDEBAR_BORDER}; padding:10px 12px; color:#9fb7ff; font-size:12px; background:#0d1f48; }

  .vv-viewer{
    margin-left:var(--sb-w);
    padding:18px clamp(12px, 2vw, 28px);
    min-height:calc(100dvh - var(--header-offset));
    display:flex; justify-content:center; align-items:flex-start;
  }
  .vv-frameWrap{ width:98%; max-width:1200px; }

  /* ===== Tipografía grande + justificada ===== */
  .vv-article h2{
    font-size:clamp(34px, 4vw, 52px);
    margin:0 0 12px; color:${TITLE_BLUE}; line-height:1.15; letter-spacing:.2px;
    text-align:center;
  }
  .vv-article h3{
    font-size:clamp(24px, 2.6vw, 34px);
    margin:20px 0 10px; color:${TITLE_BLUE}; line-height:1.25;
  }
  .vv-article h4{
    font-size:clamp(20px, 2.1vw, 26px);
    margin:14px 0 8px; color:${TITLE_BLUE}; line-height:1.3;
  }

  .vv-article p,
  .vv-article li{
    font-size:clamp(18px, 1.7vw, 22px);
    line-height:1.85;
    color:#0f2a45;
    text-align:justify;
    text-justify:inter-word;
    hyphens:auto;
    overflow-wrap:anywhere;
  }

  .vv-card{
    padding:clamp(14px,2.2vw,24px);
    background:#F7F9FF; margin-bottom:16px; border-radius:12px;
    border:1px solid #e6ebf1;
  }

  /* TOC */
  .vv-toc .vv-toc-list,
  .vv-toc .vv-toc-sublist { margin: 6px 0 0 0; padding-left: 1.25rem; }
  .vv-toc .vv-toc-sublist { margin-top: 4px; }
  .vv-toc .vv-toc-link{
    all:unset; cursor:pointer; color:#0b3ea8; border-bottom:1px dashed rgba(23,92,211,.25);
    font-size:clamp(16px, 1.4vw, 18px);
  }
  .vv-toc .vv-toc-link:hover{ color:#0a2f80; }

  .vv-figure{ margin:10px 0 16px; text-align:center; }
  .vv-figure img{
    max-width:60%; height:auto; border-radius:10px; border:1px solid #e6ebf1;
    box-shadow:0 8px 24px rgba(2,20,46,.05);
  }
  .vv-figure figcaption{
    margin-top:6px; font-size:clamp(13px, 1.1vw, 15px); color:#64748b;
    text-align:center;
  }

  .vv-list{ padding-left:1.25em; margin:8px 0; }

  .vv-table-wrap{ overflow:auto; border-radius:10px; border:1px solid #e6ebf1; background:#fff; }
  .vv-table{ width:100%; border-collapse:collapse; }
  .vv-table th, .vv-table td{
    padding:12px 14px; border-bottom:1px solid #eef2f7; text-align:left;
    font-size:clamp(16px, 1.4vw, 18px);
  }
  .vv-table thead th{ background:#f0f5ff; font-weight:700; color:#0b234e; }
  .vv-table tbody tr:hover{ background:#f9fbff; }

  .vv-code{ background:#0b1220; color:#e2e8f0; padding:14px; border-radius:10px; overflow:auto;
            border:1px solid rgba(120,170,255,.25); }
  .vv-code.small{ font-size:12px; }

  .vv-subsection{ margin-top:10px; padding-top:6px; border-top:1px dashed #dbe4f0; }

  /* ===== Inline helpers ===== */
  .vv-inline-code{
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: .92em;
    background:#0b1220;
    color:#e2e8f0;
    padding:.12em .38em;
    border-radius:6px;
    border:1px solid rgba(120,170,255,.25);
    white-space:nowrap;
  }

  .vv-gloss{
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: help;
    border-radius:4px;
    padding:0 .08em;
  }
  .vv-gloss:hover{
    background: #fff3bf;
  }
`}</style>

      {/* SIDEBAR */}
      <aside className="vv-sidebar" aria-label="Navegación de Contenidos">
        <h1 className="vv-title">
          <span>Fundamentos de Blockchain</span>
          <button
            className="vv-collapse"
            title={collapsed ? "Expandir" : "Colapsar"}
            onClick={() => setCollapsed((v) => !v)}
            aria-label="Alternar sidebar"
          >
            {collapsed ? "›" : "‹"}
          </button>
        </h1>

        <div className="vv-side-scroll">
          <ul className="vv-items">
            {contentsData.map((item) => {
              const active = selected.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    className={`vv-itemBtn ${active ? "vv-itemActive" : ""}`}
                    onClick={() => handleSelect(item)}
                    aria-current={active ? "page" : undefined}
                    title={item.title}
                  >
                    <span className="vv-dot">▣</span>
                    <span className="vv-itemLabel">{item.title}</span>
                    {active ? <span style={{ color: "#8ec5ff", fontWeight: 800 }}>•</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="vv-side-footer">
          <div>v1.0 • Blockchain Basics</div>
        </div>
      </aside>

      {/* VIEWER */}
      <main className="vv-viewer">
        <div className="vv-frameWrap">
          {selected?.type === "external" &&
          selected?.component &&
          ComponentRegistry[selected.component] ? (
            React.createElement(ComponentRegistry[selected.component])
          ) : (
            <div style={{ padding: 24, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 8 }}>
              <p style={{ margin: 0, color: "#475569" }}>
                Configura este ítem en <code>contentsData</code>.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
